import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createColumnHelper, createSortedRowModel, flexRender, rowSelectionFeature, rowSortingFeature, tableFeatures, useTable,
  type RowSelectionState, type SortingState,
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp } from "lucide-react";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { toast } from "sonner";
import { MonthPicker } from "@/components/MonthPicker";
import { PageHeader } from "@/components/PageHeader";
import { SearchInput } from "@/components/SearchInput";
import { SelectionBar } from "@/components/SelectionBar";
import { StatusDot } from "@/components/StatusDot";
import { EmptyRow, SkeletonRows } from "@/components/TableStates";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BookingActions } from "@/features/bookings/BookingActions";
import { BookingEditDialog } from "@/features/bookings/BookingEditDialog";
import { del, get } from "@/lib/api";
import { allClientsQuery, clientLabel } from "@/lib/clients";
import { currentMonth, date, eur, hours, monthLabel } from "@/lib/format";
import { bookingsQuery, matchesBooking, readBookingFilters, totals, writeBookingFilters, type BookingFilters } from "@/lib/filters";
import type { BookingRow, BookingsResult } from "@/lib/types";
import { cn } from "@/lib/utils";

// @tanstack/react-table v9 requires features to be a stable, module-scope
// reference (it drives the table's type parameters via `typeof features`).
const tableFeatureSet = tableFeatures({ rowSortingFeature, rowSelectionFeature, sortedRowModel: createSortedRowModel() });
const columnHelper = createColumnHelper<typeof tableFeatureSet, BookingRow>();

/**
 * Fixed column widths (table-layout: fixed): the description takes whatever is
 * left and truncates, so hours/amount/status can never be pushed off-screen.
 */
const COLUMN_LAYOUT: Record<string, { width?: string; align?: "right"; cell?: string }> = {
  select: { width: "2.75rem" },
  date: { width: "6.5rem" },
  clientName: { width: "15%" },
  projectName: { width: "13%" },
  description: {},
  billableMinutes: { width: "5.5rem", align: "right" },
  amountEur: { width: "7rem", align: "right" },
  status: { width: "8.5rem" },
};
const COLUMN_COUNT = Object.keys(COLUMN_LAYOUT).length;

function Status({ row }: { row: BookingRow }) {
  if (!row.invoiced) return <StatusDot tone="open">offen</StatusDot>;
  const label = row.invoiceNumber ?? "verrechnet";
  return row.invoiceUrl ? (
    <a
      href={row.invoiceUrl}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="rounded-sm underline-offset-2 hover:underline focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
      title="Rechnung in sevDesk öffnen"
    >
      <StatusDot tone="done">{label}</StatusDot>
    </a>
  ) : <StatusDot tone="done">{label}</StatusDot>;
}

function Truncated({ children }: { children: string }) {
  return <span className="block truncate" title={children}>{children}</span>;
}

export function Buchungen() {
  const fallback = currentMonth();
  const [sp, setSp] = useSearchParams();
  const f = readBookingFilters(sp);
  const setF = (patch: Partial<BookingFilters>) => setSp(writeBookingFilters({ ...f, ...patch }), { replace: true });

  const qc = useQueryClient();
  const entries = useQuery({ queryKey: ["entries", f.month, f.client, f.status], queryFn: () => get<BookingsResult>(bookingsQuery(f)) });
  // All clients incl. former ones: their bookings must stay filterable and editable.
  const clients = useQuery(allClientsQuery);
  const rows = useMemo(() => (entries.data?.rows ?? []).filter((r) => matchesBooking(r, f.q)), [entries.data, f.q]);
  const sum = totals(rows);

  const [sorting, setSorting] = useState<SortingState>([{ id: "date", desc: true }]);
  const [selection, setSelection] = useState<RowSelectionState>({});
  const [editing, setEditing] = useState<BookingRow | null>(null);
  const selected = rows.filter((r) => selection[String(r.id)]);

  const remove = useMutation({
    mutationFn: async (ids: number[]) => { for (const id of ids) await del(`/api/entries/${id}`); },
    onSuccess: (_d, ids) => toast.success(ids.length === 1 ? "Buchung gelöscht" : `${ids.length} Buchungen gelöscht`),
    onError: (e) => toast.error(e.message),
    // Runs on both success and failure: a mid-batch failure still deleted some
    // rows, so the list must refetch either way or the UI stays stale.
    onSettled: () => {
      setSelection({});
      qc.invalidateQueries({ queryKey: ["entries"] });
    },
  });

  const columns = useMemo(() => columnHelper.columns([
    columnHelper.display({
      id: "select",
      header: ({ table }) => (
        <Checkbox aria-label="Alle auswählen" checked={table.getIsAllRowsSelected() || (table.getIsSomeRowsSelected() && "indeterminate")}
          onCheckedChange={(v) => table.toggleAllRowsSelected(!!v)} />
      ),
      // Label fills the cell height so the whole cell is a hit target for the checkbox.
      cell: ({ row }) => (
        <label className="-my-2 flex h-11 cursor-pointer items-center">
          <Checkbox aria-label="Auswählen" checked={row.getIsSelected()} onCheckedChange={(v) => row.toggleSelected(!!v)} />
        </label>
      ),
      enableSorting: false,
    }),
    columnHelper.accessor("date", { header: "Datum", cell: ({ getValue }) => <span className="tabular-nums">{date(getValue())}</span> }),
    columnHelper.accessor("clientName", { header: "Kunde", cell: ({ getValue }) => <Truncated>{getValue()}</Truncated> }),
    columnHelper.accessor("projectName", { header: "Projekt", cell: ({ getValue }) => <Truncated>{getValue()}</Truncated> }),
    columnHelper.accessor("description", { header: "Tätigkeit", cell: ({ getValue }) => <Truncated>{getValue()}</Truncated> }),
    columnHelper.accessor("billableMinutes", { header: "Stunden", cell: ({ getValue }) => <span className="tabular-nums">{hours(getValue())}</span> }),
    columnHelper.accessor("amountEur", { header: "Betrag", cell: ({ getValue }) => <span className="tabular-nums">{eur(getValue())}</span> }),
    columnHelper.accessor((r) => (r.invoiced ? 1 : 0), { id: "status", header: "Status", cell: ({ row }) => <Status row={row.original} /> }),
  ]), []);

  const table = useTable({
    features: tableFeatureSet,
    data: rows,
    columns,
    state: { sorting, rowSelection: selection },
    onSortingChange: setSorting,
    onRowSelectionChange: setSelection,
    getRowId: (r) => String(r.id),
  });

  const openRow = (r: BookingRow) => { if (!r.invoiced) setEditing(r); };
  const openSelected = selected.filter((r) => !r.invoiced);
  const loading = entries.isPending;
  const period = f.month ? `im ${monthLabel(f.month)}` : "in allen Monaten";

  return (
    <div className="space-y-5">
      <PageHeader title="Buchungen" />

      <div className="flex flex-wrap items-center gap-2">
        <MonthPicker value={f.month} fallback={fallback} onChange={(month) => { setSelection({}); setF({ month }); }} />
        <Tabs value={f.status} onValueChange={(v) => { setSelection({}); setF({ status: v as BookingFilters["status"] }); }}>
          <TabsList aria-label="Status">
            <TabsTrigger value="open">Offen</TabsTrigger>
            <TabsTrigger value="invoiced">Verrechnet</TabsTrigger>
            <TabsTrigger value="all">Alle</TabsTrigger>
          </TabsList>
        </Tabs>
        <Select value={f.client === null ? "all" : String(f.client)} onValueChange={(v) => setF({ client: v === "all" ? null : Number(v) })}>
          <SelectTrigger className="w-full sm:w-52" aria-label="Kunde"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Kunden</SelectItem>
            {clients.data?.clients.map((c) => <SelectItem key={c.id} value={String(c.id)}>{clientLabel(c)}</SelectItem>)}
          </SelectContent>
        </Select>
        <SearchInput className="w-full sm:ml-auto sm:w-60" value={f.q} onChange={(q) => setF({ q })} />
      </div>

      {/* Key figures first: billable hours and money are what month-end is about. */}
      <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm sm:flex sm:flex-wrap">
        <div><dt className="text-muted-foreground">Fakturierbar</dt><dd className="text-lg font-semibold tabular-nums">{loading ? "–" : hours(sum.billableMinutes)}</dd></div>
        <div><dt className="text-muted-foreground">Betrag</dt><dd className="text-lg font-semibold tabular-nums">{loading ? "–" : eur(sum.amountEur)}</dd></div>
        <div><dt className="text-muted-foreground">Getrackt</dt><dd className="text-lg tabular-nums text-muted-foreground">{loading ? "–" : hours(sum.trackedMinutes)}</dd></div>
        <div><dt className="text-muted-foreground">Buchungen</dt><dd className="text-lg tabular-nums text-muted-foreground">{loading ? "–" : sum.count}</dd></div>
      </dl>

      {entries.isError && <p className="text-sm text-destructive">{entries.error.message}</p>}

      <div className="hidden md:block">
        <Table className="table-fixed" containerClassName="overflow-visible">
          <colgroup>
            {Object.entries(COLUMN_LAYOUT).map(([id, c]) => <col key={id} style={{ width: c.width }} />)}
          </colgroup>
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id} className="hover:bg-transparent">
                {hg.headers.map((h) => {
                  const layout = COLUMN_LAYOUT[h.column.id] ?? {};
                  const sorted = h.column.getIsSorted();
                  return (
                    <TableHead
                      key={h.id}
                      aria-sort={sorted === "asc" ? "ascending" : sorted === "desc" ? "descending" : undefined}
                      className={cn("sticky top-14 z-10 bg-background text-muted-foreground shadow-[inset_0_-1px_0_var(--border)]", layout.align === "right" && "text-right")}
                    >
                      {h.column.getCanSort() ? (
                        <button
                          type="button"
                          onClick={h.column.getToggleSortingHandler()}
                          className={cn("inline-flex items-center gap-1 rounded-sm hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none", sorted && "text-foreground", layout.align === "right" && "flex-row-reverse")}
                        >
                          {flexRender(h.column.columnDef.header, h.getContext())}
                          {sorted === "asc" ? <ArrowUp className="size-3.5" /> : sorted === "desc" ? <ArrowDown className="size-3.5" /> : <span className="size-3.5" />}
                        </button>
                      ) : flexRender(h.column.columnDef.header, h.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {loading ? <SkeletonRows columns={COLUMN_COUNT} /> : rows.length === 0 ? (
              <EmptyRow columns={COLUMN_COUNT}>
                {f.q ? `Keine Buchungen zu „${f.q}“.` : f.status === "open" ? `Nichts offen ${period} – alles verrechnet.` : `Keine Buchungen ${period}.`}
              </EmptyRow>
            ) : table.getRowModel().rows.map((r) => {
              const b = r.original;
              return (
                <TableRow
                  key={r.id}
                  data-state={r.getIsSelected() ? "selected" : undefined}
                  tabIndex={b.invoiced ? undefined : 0}
                  aria-label={b.invoiced ? undefined : `Buchung vom ${date(b.date)} bearbeiten`}
                  onClick={() => openRow(b)}
                  onKeyDown={(e) => { if (e.key === "Enter" && e.target === e.currentTarget) openRow(b); }}
                  className={cn("h-11 outline-none focus-visible:bg-muted/60", b.invoiced ? "text-muted-foreground" : "cursor-pointer")}
                >
                  {r.getAllCells().map((c) => {
                    const layout = COLUMN_LAYOUT[c.column.id] ?? {};
                    return (
                      <TableCell
                        key={c.id}
                        // The whole row opens the editor; the checkbox cell is a selection target instead.
                        onClick={c.column.id === "select" ? (e) => e.stopPropagation() : undefined}
                        className={cn("overflow-hidden", layout.align === "right" && "text-right", c.column.id === "select" && "cursor-default")}
                      >
                        {flexRender(c.column.columnDef.cell, c.getContext())}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
          {!loading && rows.length > 0 && (
            <TableFooter className="bg-transparent">
              <TableRow className="hover:bg-transparent">
                <TableCell />
                <TableCell colSpan={4} className="text-muted-foreground">Summe · {sum.count} Buchungen</TableCell>
                <TableCell className="text-right tabular-nums">{hours(sum.billableMinutes)}</TableCell>
                <TableCell className="text-right tabular-nums">{eur(sum.amountEur)}</TableCell>
                <TableCell />
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </div>

      <ul className="space-y-2 md:hidden">
        {loading && Array.from({ length: 4 }, (_, i) => <li key={i} className="h-28 animate-pulse rounded-lg bg-muted motion-reduce:animate-none" />)}
        {!loading && rows.length === 0 && <li className="py-12 text-center text-sm text-muted-foreground">Keine Buchungen {period}.</li>}
        {!loading && table.getRowModel().rows.map((r) => {
          const b = r.original;
          return (
            <li key={b.id} className={cn("flex rounded-lg border", r.getIsSelected() && "bg-muted")}>
              {/* 44px tap target around the checkbox, separate from the card's edit target. */}
              <label className="flex w-11 shrink-0 cursor-pointer justify-center pt-3.5">
                <Checkbox aria-label="Auswählen" checked={r.getIsSelected()} onCheckedChange={(v) => r.toggleSelected(!!v)} />
              </label>
              <button
                type="button"
                disabled={b.invoiced}
                onClick={() => openRow(b)}
                className="min-w-0 flex-1 space-y-1 py-3 pr-3 text-left disabled:cursor-default"
              >
                <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
                  <span className="truncate tabular-nums">{date(b.date)} · {b.clientName}</span>
                  <Status row={b} />
                </div>
                <p className={cn("line-clamp-3 text-sm", b.invoiced && "text-muted-foreground")}>{b.description}</p>
                <p className="text-sm font-medium tabular-nums">{hours(b.billableMinutes)} · {eur(b.amountEur)}</p>
              </button>
            </li>
          );
        })}
      </ul>

      <SelectionBar count={selected.length} onClear={() => setSelection({})}>
        <BookingActions selected={selected} onDone={() => setSelection({})} />
        <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" disabled={openSelected.length === 0 || remove.isPending}
          onClick={() => confirm(`${openSelected.length} offene Buchung(en) löschen?`) && remove.mutate(openSelected.map((r) => r.id))}>
          Löschen
        </Button>
      </SelectionBar>

      <BookingEditDialog row={editing} clients={clients.data?.clients ?? []} onClose={() => setEditing(null)} />
    </div>
  );
}
